import { useId, useState } from "react"
import { DownOutlined, RightOutlined, UserOutlined } from "@ant-design/icons"
import { Flex, Popconfirm, Radio, Spin, Tooltip, Typography } from "antd"
import { Alert } from "~/components/Alert"
import { useShallow } from "zustand/react/shallow"
import { ResourcePreview, ResourceUploadField, Text } from "~/components"
import { getModelFigureArtlinePointCost } from "~/common/billing/pointCosts"
import { IMAGE_UPLOAD_REQUIREMENTS } from "~/common/media/uploadedResources"
import { isImageTaskSucceededStatus } from "~/common/constants"
import {
  getRecreateArtlineInputError,
  getRecreateModelArtline,
  getRecreateModelImageUrl,
} from "~/common/video/recreate"
import { useHotVideoStore } from "~/store/hotVideo"
import { ModelPicker } from "../ModelPicker"
import { useExpandedSectionScroll } from "../useExpandedSectionScroll"
import { ProductInfo } from "./ProductInfo"
import styles from "./index.css"

export const AdvancedSettings = () => {
  const pointCost = getModelFigureArtlinePointCost()
  const disabled = useHotVideoStore(
    (state) => state.isRequesting || state.isRestoringTaskDraft,
  )
  const {
    referenceModel,
    isGeneratingModelArtline,
    modelArtlineError,
    generateModelFigureArtline,
    setReferenceModel,
    setReferenceModelMode,
  } = useHotVideoStore(
    useShallow((state) => ({
      referenceModel: state.referenceModel,
      isGeneratingModelArtline: state.isGeneratingModelArtline,
      modelArtlineError: state.modelArtlineError,
      generateModelFigureArtline: state.generateModelFigureArtline,
      setReferenceModel: state.setReferenceModel,
      setReferenceModelMode: state.setReferenceModelMode,
    })),
  )
  const artlineInputError = useHotVideoStore(getRecreateArtlineInputError)
  const artline = getRecreateModelArtline(referenceModel)
  const hasArtline = Boolean(
    artline &&
    isImageTaskSucceededStatus(artline.status) &&
    artline.result_urls.length,
  )
  const [open, setOpen] = useState(false)
  const [modelExpanded, setModelExpanded] = useState(false)
  const modelSectionRef = useExpandedSectionScroll(
    modelExpanded,
    referenceModel.mode,
  )
  const modelPanelId = useId()
  return (
    <Flex
      vertical
      gap={16}
    >
      <ProductInfo />

      <Flex
        ref={modelSectionRef}
        className={styles.section}
        vertical
        gap={12}
      >
        <button
          className={styles.sectionToggle}
          type="button"
          aria-expanded={modelExpanded}
          aria-controls={modelPanelId}
          onClick={() => setModelExpanded((value) => !value)}
        >
          <Flex
            component="span"
            align="center"
            gap={8}
          >
            <UserOutlined className={styles.sectionIcon} />
            <span>模特设置</span>
            <DownOutlined
              className={styles.sectionArrow}
              rotate={modelExpanded ? 180 : 0}
            />
          </Flex>
        </button>
        <div
          id={modelPanelId}
          hidden={!modelExpanded}
        >
          <Radio.Group
            value={referenceModel.mode}
            onChange={(event) => {
              const mode = event.target.value
              if (mode === "none" || mode === "system" || mode === "custom")
                setReferenceModelMode(mode)
            }}
            className={styles.options}
          >
            <Flex
              vertical
              gap={12}
            >
              <Flex
                vertical
                gap={4}
              >
                <Radio value="none">
                  <Text>AI 智能匹配模特</Text>
                </Radio>
                <Text
                  className={styles.optionContent}
                  type="secondary"
                  size="s"
                >
                  AI 根据参考视频智能判断是否生成模特及人脸。
                  <Text
                    color="primary"
                    size="s"
                  >
                    若原视频中无人物、仅出现手/脚，或未露出完整五官，建议勾选此项
                  </Text>
                  ，避免生成多余的人脸或人物，确保新视频与参考视频保持一致
                </Text>
              </Flex>
              <Flex
                vertical
                gap={8}
              >
                <Radio value="system">
                  <Text className={styles.optionLabel}>从系统模特库选择</Text>
                </Radio>
                {referenceModel.mode === "system" ? (
                  <Flex
                    align="center"
                    gap={12}
                    className={styles.optionContent}
                  >
                    {referenceModel.model?.display_url ? (
                      <ResourcePreview
                        type="image"
                        src={referenceModel.model.display_url}
                        alt={
                          referenceModel.model.name ||
                          referenceModel.model.model_id
                        }
                      />
                    ) : null}
                    <Typography.Link
                      className={styles.linkButton}
                      disabled={disabled}
                      onClick={() => setOpen(true)}
                    >
                      {referenceModel.model ? "重选" : "选择模特"}{" "}
                      <RightOutlined />
                    </Typography.Link>
                  </Flex>
                ) : null}
              </Flex>
              <Flex
                vertical
                gap={8}
              >
                <Radio value="custom">
                  <Text className={styles.optionLabel}>
                    上传自定义模特参考图
                  </Text>
                </Radio>
                {referenceModel.mode === "custom" ? (
                  <Flex
                    vertical
                    gap={8}
                    className={styles.optionContent}
                  >
                    <Flex
                      className={styles.modelRow}
                      gap={12}
                    >
                      {isGeneratingModelArtline ? (
                        <Flex
                          aria-busy
                          aria-label="AI转线稿图进行中"
                        >
                          <Spin description="生成中">
                            <ResourcePreview
                              type="image"
                              src={getRecreateModelImageUrl(referenceModel)}
                              alt="模特参考图"
                              preview={false}
                            />
                          </Spin>
                        </Flex>
                      ) : (
                        <ResourceUploadField
                          resourceType="image"
                          value={getRecreateModelImageUrl(referenceModel)}
                          onChange={(url) =>
                            setReferenceModel({ mode: "custom", url })
                          }
                          maxCount={1}
                          showWhiteBgAction={false}
                          requirementsText={IMAGE_UPLOAD_REQUIREMENTS}
                        />
                      )}
                      {referenceModel.url &&
                      !isGeneratingModelArtline &&
                      !hasArtline ? (
                        <Tooltip title={artlineInputError}>
                          <Popconfirm
                            title={`AI转线稿图，预计消耗${pointCost}积点`}
                            okText="确认"
                            icon={null}
                            cancelText="取消"
                            disabled={Boolean(artlineInputError)}
                            onConfirm={() => void generateModelFigureArtline()}
                          >
                            <Typography.Link
                              className={styles.linkButton}
                              disabled={Boolean(artlineInputError)}
                            >
                              AI转线稿图 <RightOutlined />
                            </Typography.Link>
                          </Popconfirm>
                        </Tooltip>
                      ) : null}
                    </Flex>
                    {referenceModel.url && modelArtlineError ? (
                      <Alert
                        level="strong"
                        showIcon
                        title={modelArtlineError}
                      />
                    ) : null}
                    <Text
                      size="s"
                      type="secondary"
                    >
                      部分生视频模型不支持传真人参考图，则可以转成人物线稿图传入。
                      <Typography.Link
                        className={styles.modelHelpLink}
                        href="https://meidengtech.feishu.cn/wiki/EsFewD7bPi90Y0klOY7ccgL7nAd#share-KPl4dLZM4o1Varxu8tOcOzzGnTc"
                        target="_blank"
                        rel="noreferrer"
                      >
                        查看哪些模型支持真人参考图 <RightOutlined />
                      </Typography.Link>
                    </Text>
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
          </Radio.Group>
        </div>
      </Flex>
      {open && referenceModel.mode === "system" ? (
        <ModelPicker
          selected={referenceModel.model?.model_id}
          onCancel={() => setOpen(false)}
          onConfirm={(model) => {
            setReferenceModel({ mode: "system", model })
            setOpen(false)
          }}
        />
      ) : null}
    </Flex>
  )
}
